/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    Button,
    Flex,
    ChakraProvider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import theme from '../../../theme'
import {getPDPath} from '../../../utils/custom-utils'

/**
 * Image with text component
 *
 * @param {object} props
 * @param {string} - props.ITCLink - Image Link.
 * @param {string} - props.title - Text Below Image.
 * @param {image} - props.image - Image.
 * @param {string} - props.heading - Text Overlay.
 * @param {string} - props.altText - The image alt text shown by the component.
 * @returns {React.ReactElement} - ImageWithText component.
 */
export const ImageWithTextWithOutOverlay = ({
    custom,
    altText,
    description,
    title,
    backgroundColor,
    textColor,
    richText,
    buttonText,
    categoryLink,
    imageToRight
}) => {
    const {site, locale} = useMultiSite()
    const hasCaption = title
    const text = richText
    const bgColor = backgroundColor
    const textUrl = getPDPath(categoryLink, site, locale)
    const segment = useSegment()

    return (
        <ChakraProvider theme={theme}>
            <Box className={'image-with-text'} bg={bgColor ? bgColor : ''}>
                <Flex
                    alignItems={'center'}
                    gap={'15'}
                    flexDirection={{base: 'column', lg: imageToRight ? 'row-reverse' : 'row'}}
                    justifyContent={{base: 'center', lg: 'flex-start'}}
                >
                    <Box
                        className={'image-with-text-figure'}
                        width={{base: '100%', lg: '60%'}}
                        pl={{base: 0, lg: 4}}
                        pr={{base: 0, lg: 0}}
                        onClick={(event) => {
                            // segment
                            segment.sendPanelSelectedEvent(event, {})
                        }}
                    >
                        <Link to={textUrl} aria-label="Shop Women's Clothing">
                            <img
                                alt={altText || 'Womens clothing'}
                                src={custom?.image?.disBaseLink}
                                width="100%"
                            />
                        </Link>
                    </Box>
                    <Box
                        className={'image-with-text-figure 77777 '}
                        width={{base: '100%', lg: '40%'}}
                        pl={4}
                        pr={{base: 0, lg: imageToRight ? 4 : 0}}
                        mb={{base: '4', lg: 0}}
                        textAlign={{base: 'center', lg: 'left'}}
                        onClick={(event) => {
                            // segment
                            segment.sendPanelSelectedEvent(event, {})
                        }}
                    >
                        {hasCaption && (
                            <Box>
                                {title && (
                                    <Text
                                        mb={'2'}
                                        textColor={textColor}
                                        fontSize={'2em'}
                                        fontWeight={'medium'}
                                        dangerouslySetInnerHTML={{__html: title}}
                                    />
                                )}
                                {description && (
                                    <Text
                                        textColor={textColor}
                                        fontSize={'1em'}
                                        fontWeight={'normal'}
                                        mb={'1rem'}
                                        dangerouslySetInnerHTML={{__html: description}}
                                    />
                                )}
                                {text && (
                                    <Text
                                        as="span"
                                        textColor={textColor}
                                        dangerouslySetInnerHTML={{__html: richText.value}}
                                        sx={{
                                            p: {
                                                display: 'flex',
                                                alignItems: 'left',
                                                justifyContent: 'left'
                                            }
                                        }}
                                    />
                                )}
                                {buttonText && (
                                    <Button variant="primary" as={Link} to={textUrl?.url}>
                                        {buttonText}
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>
                </Flex>
            </Box>
        </ChakraProvider>
    )
}

ImageWithTextWithOutOverlay.propTypes = {
    ITCLink: PropTypes.string,
    title: PropTypes.string,
    richText: PropTypes.object,
    custom: PropTypes.object,
    heading: PropTypes.string,
    altText: PropTypes.string,
    description: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    buttonText: PropTypes.string,
    categoryLink: PropTypes.string,
    imageToRight: PropTypes.bool,
    buttonSize: PropTypes.string
}

export default ImageWithTextWithOutOverlay
